import {createContext, useCallback, useContext, useMemo, useRef, useState} from 'react'

import PropTypes from 'prop-types'

import {buyerIntentionsSelectionPropType} from '../contracts.js'

export const CONFIG_KEYS = [
  'adId',
  'contractId',
  'pageName',
  'offerType',
  'target',
  'phone',
  'hidePhone',
  'multiprovince',
  'extendedLeadTrackingData',
  'hasCriteoEvents',
  'underlineRevealedPhone',
  'authenticationHitInformation2',
  'enableAuthBeforeLead',
  'isPrivateSeller',
  'onContact',
  'onLeadPhoneCall',
  'onWhatsappError',
  'onSendMessageClick',
  'onLeadError',
  'onSubmitError',
  'onAlertCreated'
]

export const CARD_INPUT_KEYS = [
  'sellerName',
  'sellerLogo',
  'sellerPhoto',
  'location',
  'ratingAverage',
  'ratingCount',
  'onShowAllRatings',
  'hasHomeDelivery',
  'isCanarias',
  'pack',
  'onOpenDrawer',
  'alertSearch',
  'formPresentation',
  'isZipCodeEnabled',
  'showTargetBuyerOptions',
  'targetBuyer',
  'subscriptionCommitment',
  'enableAlertCreation',
  'isSellerBlocked',
  'sellerId',
  'userData',
  'buyerIntentions',
  'isLeadEnrichmentEnabled',
  'isVOorVNorKM0',
  'isRenting',
  'isDrawerOpen',
  'onDrawerOpenChange',
  'onTargetBuyerChange',
  'presetBuyerIntentions',
  'presetMessage',
  'availableActions'
]

const pickKeys = (keys, source) => Object.fromEntries(keys.map(key => [key, source?.[key]]))

export const pickContactSellerV2Config = source => pickKeys(CONFIG_KEYS, source)

const useValueMemoizedByKeys = (keys, value) => {
  const values = keys.map(key => value?.[key])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => Object.fromEntries(keys.map((key, index) => [key, values[index]])), values)
}

const ContactSellerV2ConfigContext = createContext(undefined)
ContactSellerV2ConfigContext.displayName = 'ContactSellerV2ConfigContext'

const ContactCardInputContext = createContext(undefined)
ContactCardInputContext.displayName = 'ContactCardInputContext'

const ContactCardStateContext = createContext(undefined)
ContactCardStateContext.displayName = 'ContactCardStateContext'

export const ContactSellerV2ConfigProvider = ({value, children}) => {
  const memoized = useValueMemoizedByKeys(CONFIG_KEYS, value)

  return <ContactSellerV2ConfigContext.Provider value={memoized}>{children}</ContactSellerV2ConfigContext.Provider>
}

ContactSellerV2ConfigProvider.displayName = 'ContactSellerV2ConfigProvider'
ContactSellerV2ConfigProvider.propTypes = {value: PropTypes.object, children: PropTypes.node}

export const ContactCardInputProvider = ({value, children}) => {
  const memoized = useValueMemoizedByKeys(CARD_INPUT_KEYS, value)

  return <ContactCardInputContext.Provider value={memoized}>{children}</ContactCardInputContext.Provider>
}

ContactCardInputProvider.displayName = 'ContactCardInputProvider'
ContactCardInputProvider.propTypes = {value: PropTypes.object, children: PropTypes.node}

export const ContactCardStateProvider = ({initialSelection, onBuyerIntentionsChange, children}) => {
  const [buyerIntentionsSelection, setSelection] = useState(initialSelection)
  const onBuyerIntentionsChangeRef = useRef(onBuyerIntentionsChange)
  onBuyerIntentionsChangeRef.current = onBuyerIntentionsChange

  const setBuyerIntentionsSelection = useCallback(selection => {
    setSelection(selection)
    onBuyerIntentionsChangeRef.current?.(selection)
  }, [])

  const value = useMemo(
    () => ({buyerIntentionsSelection, setBuyerIntentionsSelection}),
    [buyerIntentionsSelection, setBuyerIntentionsSelection]
  )

  return <ContactCardStateContext.Provider value={value}>{children}</ContactCardStateContext.Provider>
}

ContactCardStateProvider.displayName = 'ContactCardStateProvider'
ContactCardStateProvider.propTypes = {
  initialSelection: buyerIntentionsSelectionPropType,
  onBuyerIntentionsChange: PropTypes.func,
  children: PropTypes.node
}

export const useContactSellerV2Config = () => {
  const value = useContext(ContactSellerV2ConfigContext)

  if (value === undefined) {
    throw new Error('useContactSellerV2Config must be used inside <ContactSellerV2ConfigProvider>')
  }

  return value
}

export const useContactCardInput = () => {
  const value = useContext(ContactCardInputContext)

  if (value === undefined) {
    throw new Error('useContactCardInput must be used inside <ContactCardInputProvider>')
  }

  return value
}

export const useContactCardState = ({optional = false} = {}) => {
  const value = useContext(ContactCardStateContext)

  if (value === undefined && !optional) {
    throw new Error('useContactCardState must be used inside <ContactCardStateProvider>')
  }

  return value
}
